<template>
    <span ref="target">
        <span
            @mouseover="op?.show"
            @mouseout="op?.hide"
        >
            <template v-if="outsideLink">
                <NuxtLink
                    :to="outsideLink"
                    external
                    target="_blank"
                    class="!border-b-0 !no-underline"
                >
                    [{{ bgText }}]</NuxtLink
                >
            </template>
            <template v-else>[{{ bgText }}]</template>
        </span>
    </span>
    <OverlayPanel
        ref="op"
        :pt
    >
        <span v-html="formattedContent" />
    </OverlayPanel>
</template>

<script lang="ts" setup>
import { useElementVisibility, watchOnce } from "@vueuse/core"
import OverlayPanel from "primevue/overlaypanel"

const props = defineProps<{
    text: string
    tema: TemaItem
}>()

const { locale, t } = useI18n()
const store = useMainStore()

const target = ref<HTMLElement | null>(null)
const targetIsVisible = useElementVisibility(target)
const op = ref<OverlayPanel | null>(null)

watchOnce(targetIsVisible, () => {
    if (targetIsVisible) {
        getBibliografieContent()
    }
})

// const bgId = props.text.match(/\d+/g)?.[0]
const bgText = props.text.match(/\((.*?)\)/)?.[1]

const BG_ENDPOINT = "bibliografie"

const bgContent = ref<BibliografieItem[]>([])

async function getBibliografieContent() {
    if (bgContent.value.length > 0) return
    const kod = bgText?.split(" ")?.[0]
    if (!kod) return
    const data = await $fetch<BibliografieItem[]>(
        `${BG_ENDPOINT}/${props.tema.KNIHA_ID}/${locale.value === "cs" ? "cz" : locale.value}/${kod}`,
        {
            baseURL: import.meta.client
                ? store.apiBaseUrl
                : store.apiBaseUrlInt,
        }
    )
    bgContent.value = data
    return data
}

const formattedContent = computed(() => {
    if (bgContent.value.length > 0) {
        let text = bgContent.value[0]?.BIBLIO
        if (!text) return ""
        if (text.endsWith(".")) {
            text = text.slice(0, -1) // Remove the last character (".") if it exists
        }
        return text.replace(
            /(https?:\/\/|www\.)+[^\s]+/g,
            "<br>" + t("texty.biblio_online_tooltip")
        )
    }
    return ""
})

const outsideLink = computed(() => {
    if (bgContent.value[0]?.BIBLIO.includes("https://")) {
        const url = bgContent.value[0]?.BIBLIO.match(
            /(https:\/\/[^\s]+)/
        )?.[1] as string
        if (url.endsWith(".")) {
            return url.slice(0, -1) // Remove the last character (".") if it exists
        }
        return url
    }
    if (bgContent.value[0]?.BIBLIO.includes("www.")) {
        const url =
            "https://" + bgContent.value[0]?.BIBLIO.match(/(www.[^\s]+)/)?.[1]
        if (url.endsWith(".")) {
            return url.slice(0, -1) // Remove the last character (".") if it exists
        }
        return url
    }
    return null
})

const pt = {
    root: {
        class: [
            // Shape
            "rounded-md shadow-lg max-w-[50vw] md:max-w-[34rem]",
            "border-0",

            // Position
            "absolute -translate-x-5 top-0 mt-2",
            "z-40 transform origin-center",

            // Color
            "bg-white",
            "text-black-text",

            // Before: Triangle
            "before:absolute before:-top-[9px] before:-ml-[9px] before:left-[calc(var(--overlayArrowLeft,0)+0.75rem)] z-0",
            "before:w-0 before:h-0",
            "before:border-transparent before:border-solid",
            "before:border-x-[4px] before:border-[4px]",
            "before:border-t-0 before:border-b-gray-300/10 ",

            "after:absolute after:-top-1 after:-ml-[4px] after:left-[calc(var(--overlayArrowLeft,0)+0.75rem)]",
            "after:w-0 after:h-0",
            "after:border-transparent after:border-solid",
            "after:border-x-[0.25rem] after:border-[0.25rem]",
            "after:border-t-0 after:border-b-black-text ",
        ],
    },
    content: {
        class: "p-2 border text-sm items-center flex bg-white border border-black-text break-[break-words]",
    },
    transition: {
        enterFromClass: "opacity-0 scale-y-[0.8]",
        enterActiveClass:
            "transition-[transform,opacity] duration-[120ms] ease-[cubic-bezier(0,0,0.2,1)]",
        leaveActiveClass: "transition-opacity duration-100 ease-linear",
        leaveToClass: "opacity-0",
    },
}
</script>

<style scoped></style>
